import React, { useState, useEffect } from 'react';

const DivisionPositionModal = ({
  shift,
  onClose,
  onSave,
  BASE_URL,
  overlayZClass = "z-[10001]",
}) => {
  const [divisions, setDivisions] = useState([]);
  const [positions, setPositions] = useState([]);
  
  // Get IDs from shift
  const initialDivisionId = shift?.division_id || '';
  const initialPositionId = shift?.position_id || '';
  
  // Get names for display
  const savedDivisionName = shift?.division_name || '';
  const savedPositionName = shift?.position_name || '';
  
  const [selectedDivisionId, setSelectedDivisionId] = useState(initialDivisionId);
  const [selectedPositionId, setSelectedPositionId] = useState(initialPositionId);
  const [divisionsLoading, setDivisionsLoading] = useState(true);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [error, setError] = useState('');

  // Debug log
  useEffect(() => {
    console.log('🔍 [DivisionPositionModal] Shift data:', {
      division_id: initialDivisionId,
      division_name: savedDivisionName,
      position_id: initialPositionId,
      position_name: savedPositionName,
    });
  }, [shift, initialDivisionId, initialPositionId, savedDivisionName, savedPositionName]);

  // Fetch divisions on mount
  useEffect(() => {
    const fetchDivisions = async () => {
      try {
        setDivisionsLoading(true);
        setError('');
        console.log('📥 Fetching divisions from:', `${BASE_URL}/api/divisions`);
        const res = await fetch(`${BASE_URL}/api/divisions`);
        if (!res.ok) throw new Error('Failed to fetch divisions');
        const data = await res.json();
        const divArray = Array.isArray(data) ? data : data.data || [];
        console.log('✅ Divisions loaded:', divArray.length);
        console.log('📋 All divisions:', divArray);
        
        setDivisions(divArray);

        // Debug: show actual structure
        if (divArray.length > 0) {
          console.log('🔍 First division object:', divArray[0]);
        }

        // Try to auto-select division by ID first, then by name
        if (initialDivisionId) {
          // Try to find by ID (the API returns division_id)
          let foundByID = divArray.find(d => String(d.division_id) === String(initialDivisionId));
          
          if (foundByID) {
            const divId = foundByID.division_id;
            console.log('✅ Found division by ID:', divId, foundByID.name);
            setSelectedDivisionId(divId);
          } else {
            console.warn('⚠️ Division ID', initialDivisionId, 'not found, trying by name:', savedDivisionName);
            const foundByName = divArray.find(d => d.name === savedDivisionName);
            if (foundByName) {
              const divId = foundByName.division_id;
              console.log('✅ Found division by name:', divId, foundByName.name);
              setSelectedDivisionId(divId);
            } else {
              console.error('❌ Division not found by ID or name');
              console.log('📋 Available divisions:', divArray.map(d => ({ division_id: d.division_id, name: d.name })));
            }
          }
        }
      } catch (err) {
        console.error('❌ Error fetching divisions:', err);
        setError('Failed to load divisions');
        setDivisions([]);
      } finally {
        setDivisionsLoading(false);
      }
    };
    fetchDivisions();
  }, [BASE_URL, initialDivisionId, savedDivisionName]);

  // Fetch positions when division changes
  useEffect(() => {
    if (!selectedDivisionId) {
      console.log('⚠️ No division selected, clearing positions');
      setPositions([]);
      return;
    }

    const fetchPositions = async () => {
      try {
        setPositionsLoading(true);
        setError('');
        const url = `${BASE_URL}/api/divisions/${selectedDivisionId}/positions`;
        console.log('📥 Fetching positions from:', url);
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Failed to fetch positions: ${res.status}`);
        const data = await res.json();
        const posArray = Array.isArray(data) ? data : data.data || [];
        console.log('✅ Positions loaded for division', selectedDivisionId, ':', posArray.length);
        console.log('📋 All positions:', posArray);
        setPositions(posArray);

        // Try to auto-select position by ID first, then by name
        if (initialPositionId && posArray.length > 0) {
          // The API returns position_id (not id)
          const foundByID = posArray.find(p => String(p.position_id) === String(initialPositionId));
          if (foundByID) {
            const posId = foundByID.position_id;
            console.log('✅ Found position by ID:', posId, foundByID.name);
            setSelectedPositionId(posId);
          } else {
            console.warn('⚠️ Position ID', initialPositionId, 'not found, trying by name:', savedPositionName);
            const foundByName = posArray.find(p => p.name === savedPositionName);
            if (foundByName) {
              const posId = foundByName.position_id;
              console.log('✅ Found position by name:', posId, foundByName.name);
              setSelectedPositionId(posId);
            } else {
              console.error('❌ Position not found by ID or name');
              console.log('📋 Available positions:', posArray.map(p => ({ position_id: p.position_id, name: p.name })));
            }
          }
        }
      } catch (err) {
        console.error('❌ Error fetching positions:', err);
        setError(`Failed to load positions: ${err.message}`);
        setPositions([]);
      } finally {
        setPositionsLoading(false);
      }
    };

    fetchPositions();
  }, [selectedDivisionId, BASE_URL, initialPositionId, savedPositionName]);

  // Handle division change
  const handleDivisionChange = (e) => {
    const divisionId = e.target.value;
    console.log('👤 Division changed to:', divisionId);
    setSelectedDivisionId(divisionId);
    setSelectedPositionId('');
  };

  // Handle position change
  const handlePositionChange = (e) => {
    const positionId = e.target.value;
    console.log('📍 Position changed to:', positionId);
    setSelectedPositionId(positionId);
  };

  // Handle save
  const handleSave = async () => {
    if (!selectedDivisionId || !selectedPositionId) {
      setError('Please select both Division and Position');
      return;
    }

    try {
      // Validate position belongs to division
      const selectedPosition = positions.find(p => String(p.position_id) === String(selectedPositionId));
      if (!selectedPosition) {
        setError('Invalid position selection');
        return;
      }

      const updatedShift = {
        ...shift,
        division_id: selectedDivisionId,
        position_id: selectedPositionId,
      };

      console.log('💾 Saving:', updatedShift);
      onSave(updatedShift);
    } catch (err) {
      console.error('❌ Error saving:', err);
      setError('Failed to save changes');
    }
  };

  const selectedDivision = divisions.find(d => String(d.id) === String(selectedDivisionId));
  const locationName = selectedDivision?.location_name || 'Location';

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 ${overlayZClass} flex items-center justify-center`}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Modify Division and Position</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            ✖
          </button>
        </div>

        {/* Location display */}
        <div className="text-sm text-gray-600 mb-4">{locationName}</div>

        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Division Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Division<span className="text-red-500">*</span>
            </label>
            <select
              value={selectedDivisionId}
              onChange={handleDivisionChange}
              disabled={divisionsLoading}
              className="w-full border border-gray-300 rounded px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">
                {divisionsLoading ? 'Loading divisions...' : 'Select division'}
              </option>
              {divisions.map(div => (
                <option key={div.division_id} value={div.division_id}>
                  {div.name}
                </option>
              ))}
            </select>
          </div>

          {/* Position Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Position<span className="text-red-500">*</span>
            </label>
            <select
              value={selectedPositionId}
              onChange={handlePositionChange}
              disabled={!selectedDivisionId || positionsLoading}
              className="w-full border border-gray-300 rounded px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">
                {!selectedDivisionId
                  ? 'Select division first'
                  : positionsLoading
                    ? 'Loading positions...'
                    : positions.length === 0
                      ? 'No positions available'
                      : 'Select position'}
              </option>
              {positions.map(pos => (
                <option key={pos.position_id} value={pos.position_id}>
                  {pos.name}
                </option>
              ))}
            </select>
          </div>

          {/* Helper text */}
          {!selectedDivisionId && (
            <p className="text-xs text-gray-500 italic">
              👉 Choose a division first to enable position selection
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!selectedDivisionId || !selectedPositionId}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default DivisionPositionModal;
